import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import {
  Box,
  Checkbox,
  Field,
  Flex,
  IconButton,
  Separator,
  SimpleGrid,
  Spinner,
  Table,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useFormContext } from 'react-hook-form';
import { SelectInput, Button as ButtonComponent, InputField, TextareaField } from '@themes/aegix';
import { Switch } from '@chakra-ui/react';
import { HiTrash } from 'react-icons/hi2';
import { DrawerComponent } from '../ui/drawerComponent';
import type {
  PlanTemplateFormValues,
  PlanRoleRequirement,
  PlanStationRequirement,
  StaffRole,
  IncidentType,
  ReunificationMode,
  PlanScale,
} from '../../types/ReunificationTypes';
import {
  INCIDENT_TYPE_OPTIONS,
  REUNIFICATION_MODE_OPTIONS,
  PLAN_SCALE_OPTIONS,
  STAFF_ROLE_OPTIONS,
  STATION_TYPE_OPTIONS,
  NOTIFICATION_CHANNEL_OPTIONS,
} from '../../types/ReunificationTypes';
import {
  useRoleRequirements,
  useCreateRoleRequirement,
  useUpdateRoleRequirement,
  useDeleteRoleRequirement,
  useStationRequirements,
  useCreateStationRequirement,
  useUpdateStationRequirement,
  useDeleteStationRequirement,
} from '../../hooks/queries/useReunification';
import { ApplicableSitesSection } from '../reunification/ApplicableSitesSection';
import { LabelTemplatesSection } from '../reunification/LabelTemplatesSection';
import { FloorplansSection } from '../reunification/floorplan-editor';

interface PlanTemplateFormProps {
  templateId?: string;
}

function RoleRequirementRow({
  requirement,
  templateId,
}: {
  requirement: PlanRoleRequirement;
  templateId: string;
}) {
  const { t } = useTranslation();
  const updateMutation = useUpdateRoleRequirement(templateId);
  const deleteMutation = useDeleteRoleRequirement(templateId);

  return (
    <Table.Row
      borderBottomWidth="1px"
      borderColor="border.primary"
      data-e2e={`role-requirement-${requirement.role}`}
    >
      <Table.Cell py={3}>
        <Text fontSize="sm" color="text.primary">
          {requirement.role_display}
        </Text>
      </Table.Cell>
      <Table.Cell py={3}>
        <InputField aria-label={`Minimum count for ${requirement.role_display}`}
          size="sm"
          type="number"
          min={0}
          width="70px"
          defaultValue={requirement.min_count}
          onBlur={(e) => {
            const val = parseInt(e.target.value, 10);
            if (!isNaN(val) && val !== requirement.min_count) {
              updateMutation.mutate({
                id: requirement.public_id,
                data: { min_count: val },
              });
            }
          }}
          data-e2e="minimum-count-for-requirementroledisplay-input" />
      </Table.Cell>
      <Table.Cell py={3}>
        <InputField aria-label={`Recommended count for ${requirement.role_display}`}
          size="sm"
          type="number"
          min={0}
          width="70px"
          defaultValue={requirement.recommended_count}
          onBlur={(e) => {
            const val = parseInt(e.target.value, 10);
            if (!isNaN(val) && val !== requirement.recommended_count) {
              updateMutation.mutate({
                id: requirement.public_id,
                data: { recommended_count: val },
              });
            }
          }}
          data-e2e="recommended-count-for-requirementroledisplay-input" />
      </Table.Cell>
      <Table.Cell py={3}>
        <IconButton
          aria-label={t('reunification.removeRole', 'Remove role')}
          size="xs"
          variant="ghost"
          colorPalette="red"
          onClick={() => deleteMutation.mutate(requirement.public_id)}
        >
          <HiTrash />
        </IconButton>
      </Table.Cell>
    </Table.Row>
  );
}

function AddRolesDrawer({
  isOpen,
  onClose,
  availableRoles,
  onAdd,
}: {
  isOpen: boolean;
  onClose: () => void;
  availableRoles: { label: string; value: string }[];
  onAdd: (roles: string[]) => void;
}) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const toggle = (value: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  };

  const handleAdd = () => {
    onAdd(Array.from(selected));
    setSelected(new Set());
    onClose();
  };

  const handleClose = () => {
    setSelected(new Set());
    onClose();
  };

  return (
    <DrawerComponent
      isOpen={isOpen}
      onClose={handleClose}
      title={t('reunification.addRoles', 'Add Roles')}
      size="sm"
      footer={
        <SimpleGrid columns={2} gap={4} width="100%">
          <ButtonComponent
            visual="secondary"
            type="button"
            onClick={handleClose}
            data-e2e="cancel-add-roles-button"
          >
            {t('common.cancel', 'Cancel')}
          </ButtonComponent>
          <ButtonComponent
            visual="primary"
            type="button"
            onClick={handleAdd}
            disabled={selected.size === 0}
            data-e2e="confirm-add-roles-button"
          >
            {t('reunification.addWithCount', 'Add {{countText}}', {
              countText: selected.size > 0 ? `(${selected.size})` : '',
            })}
          </ButtonComponent>
        </SimpleGrid>
      }
    >
      <VStack gap={3} align="stretch">
        {availableRoles.map((role) => (
          <Checkbox.Root
            key={role.value}
            checked={selected.has(role.value)}
            onCheckedChange={() => toggle(role.value)}
            cursor="pointer"
          >
            <Checkbox.HiddenInput />
            <Checkbox.Control borderRadius="md" />
            <Checkbox.Label fontSize="sm" color="text.primary">
              {role.label}
            </Checkbox.Label>
          </Checkbox.Root>
        ))}
      </VStack>
    </DrawerComponent>
  );
}

function RoleRequirementsSection({ templateId }: { templateId: string }) {
  const { t } = useTranslation();
  const { data: requirements, isLoading } = useRoleRequirements(templateId);
  const createMutation = useCreateRoleRequirement(templateId);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const usedRoles = new Set(requirements?.map((r) => r.role) ?? []);
  const availableRoles = STAFF_ROLE_OPTIONS.filter(
    (opt) => !usedRoles.has(opt.value as StaffRole),
  );

  const handleAddRoles = (roles: string[]) => {
    for (const role of roles) {
      createMutation.mutate({
        role: role as StaffRole,
        min_count: 1,
        recommended_count: 1,
      });
    }
  };

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={3}>
        <Text fontWeight="semibold" fontSize="sm">
          {t('reunification.roleRequirements', 'Role Requirements')}
        </Text>
        {availableRoles.length > 0 && (
          <ButtonComponent
            visual="secondary"
            size="sm"
            type="button"
            onClick={() => setDrawerOpen(true)}
            data-e2e="add-roles-button"
          >
            {t('reunification.addRoles', 'Add Roles')}
          </ButtonComponent>
        )}
      </Flex>

      {isLoading ? (
        <Spinner size="sm" />
      ) : requirements && requirements.length > 0 ? (
        <Box
          borderWidth="1px"
          borderColor="border.primary"
          borderRadius="lg"
          overflow="hidden"
          bg="surface.primary"
          maxHeight="320px"
          overflowY="auto"
        >
          <Table.Root size="sm">
            <Table.Header>
              <Table.Row bg="surface.secondary">
                <Table.ColumnHeader>
                  <Text fontSize="xs" fontWeight="semibold" color="text.secondary" textTransform="uppercase">
                    {t('common.role', 'Role')}
                  </Text>
                </Table.ColumnHeader>
                <Table.ColumnHeader width="90px">
                  <Text fontSize="xs" fontWeight="semibold" color="text.secondary" textTransform="uppercase">
                    {t('reunification.min', 'Min')}
                  </Text>
                </Table.ColumnHeader>
                <Table.ColumnHeader width="90px">
                  <Text fontSize="xs" fontWeight="semibold" color="text.secondary" textTransform="uppercase">
                    {t('reunification.goal', 'Goal')}
                  </Text>
                </Table.ColumnHeader>
                <Table.ColumnHeader width="50px" />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {requirements.map((req) => (
                <RoleRequirementRow
                  key={req.public_id}
                  requirement={req}
                  templateId={templateId}
                />
              ))}
            </Table.Body>
          </Table.Root>
        </Box>
      ) : (
        <Text fontSize="sm" color="text.secondary">
          {t('reunification.noRoleRequirementsConfigured', 'No role requirements configured')}
        </Text>
      )}

      <AddRolesDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        availableRoles={availableRoles}
        onAdd={handleAddRoles}
      />
    </Box>
  );
}

function StationRequirementRow({
  requirement,
  templateId,
}: {
  requirement: PlanStationRequirement;
  templateId: string;
}) {
  const { t } = useTranslation();
  const updateMutation = useUpdateStationRequirement(templateId);
  const deleteMutation = useDeleteStationRequirement(templateId);

  return (
    <Table.Row
      borderBottomWidth="1px"
      borderColor="border.primary"
      data-e2e={`station-requirement-${requirement.station_type}`}
    >
      <Table.Cell py={3}>
        <Text fontSize="sm" color="text.primary">
          {requirement.station_type_display}
        </Text>
      </Table.Cell>
      <Table.Cell py={3}>
        <InputField aria-label={`Minimum count for ${requirement.station_type_display}`}
          size="sm"
          type="number"
          min={0}
          width="70px"
          defaultValue={requirement.min_count}
          onBlur={(e) => {
            const val = parseInt(e.target.value, 10);
            if (!isNaN(val) && val !== requirement.min_count) {
              updateMutation.mutate({
                id: requirement.public_id,
                data: { min_count: val },
              });
            }
          }}
          data-e2e="minimum-count-for-requirementstationtypedisplay-input" />
      </Table.Cell>
      <Table.Cell py={3}>
        <InputField aria-label={`Recommended count for ${requirement.station_type_display}`}
          size="sm"
          type="number"
          min={0}
          width="70px"
          defaultValue={requirement.recommended_count}
          onBlur={(e) => {
            const val = parseInt(e.target.value, 10);
            if (!isNaN(val) && val !== requirement.recommended_count) {
              updateMutation.mutate({
                id: requirement.public_id,
                data: { recommended_count: val },
              });
            }
          }}
          data-e2e="recommended-count-for-requirementstationtypedisplay-input" />
      </Table.Cell>
      <Table.Cell py={3}>
        <IconButton
          aria-label={t('reunification.removeStation', 'Remove station')}
          size="xs"
          variant="ghost"
          colorPalette="red"
          onClick={() => deleteMutation.mutate(requirement.public_id)}
        >
          <HiTrash />
        </IconButton>
      </Table.Cell>
    </Table.Row>
  );
}

function AddStationsDrawer({
  isOpen,
  onClose,
  availableStations,
  onAdd,
}: {
  isOpen: boolean;
  onClose: () => void;
  availableStations: { label: string; value: string }[];
  onAdd: (stationTypes: string[]) => void;
}) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const toggle = (value: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  };

  const handleAdd = () => {
    onAdd(Array.from(selected));
    setSelected(new Set());
    onClose();
  };

  const handleClose = () => {
    setSelected(new Set());
    onClose();
  };

  return (
    <DrawerComponent
      isOpen={isOpen}
      onClose={handleClose}
      title={t('reunification.addStations', 'Add Stations')}
      size="sm"
      footer={
        <SimpleGrid columns={2} gap={4} width="100%">
          <ButtonComponent
            visual="secondary"
            type="button"
            onClick={handleClose}
            data-e2e="cancel-add-stations-button"
          >
            {t('common.cancel', 'Cancel')}
          </ButtonComponent>
          <ButtonComponent
            visual="primary"
            type="button"
            onClick={handleAdd}
            disabled={selected.size === 0}
            data-e2e="confirm-add-stations-button"
          >
            {t('reunification.addWithCount', 'Add {{countText}}', {
              countText: selected.size > 0 ? `(${selected.size})` : '',
            })}
          </ButtonComponent>
        </SimpleGrid>
      }
    >
      <VStack gap={3} align="stretch">
        {availableStations.map((station) => (
          <Checkbox.Root
            key={station.value}
            checked={selected.has(station.value)}
            onCheckedChange={() => toggle(station.value)}
            cursor="pointer"
          >
            <Checkbox.HiddenInput />
            <Checkbox.Control borderRadius="md" />
            <Checkbox.Label fontSize="sm" color="text.primary">
              {station.label}
            </Checkbox.Label>
          </Checkbox.Root>
        ))}
      </VStack>
    </DrawerComponent>
  );
}

function StationRequirementsSection({ templateId }: { templateId: string }) {
  const { t } = useTranslation();
  const { data: requirements, isLoading } = useStationRequirements(templateId);
  const createMutation = useCreateStationRequirement(templateId);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const usedTypes = new Set(requirements?.map((r) => r.station_type) ?? []);
  const availableStations = STATION_TYPE_OPTIONS.filter(
    (opt) => !usedTypes.has(opt.value),
  );

  const handleAddStations = (stationTypes: string[]) => {
    for (const stationType of stationTypes) {
      createMutation.mutate({
        station_type: stationType,
        min_count: 1,
        recommended_count: 1,
      });
    }
  };

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={3}>
        <Text fontWeight="semibold" fontSize="sm">
          {t('reunification.stationRequirements', 'Station Requirements')}
        </Text>
        {availableStations.length > 0 && (
          <ButtonComponent
            visual="secondary"
            size="sm" // Added size="sm" to match RoleRequirementsSection
            type="button"
            onClick={() => setDrawerOpen(true)}
            data-e2e="add-stations-button"
          >
            {t('reunification.addStations', 'Add Stations')}
          </ButtonComponent>
        )}
      </Flex>

      {isLoading ? (
        <Spinner size="sm" />
      ) : requirements && requirements.length > 0 ? (
        <Box
          borderWidth="1px"
          borderColor="border.primary"
          borderRadius="lg"
          overflow="hidden"
          bg="surface.primary"
          maxHeight="320px"
          overflowY="auto"
        >
          <Table.Root size="sm">
            <Table.Header>
              <Table.Row bg="surface.secondary">
                <Table.ColumnHeader>
                  <Text fontSize="xs" fontWeight="semibold" color="text.secondary" textTransform="uppercase">
                    {t('reunification.stationType', 'Station Type')}
                  </Text>
                </Table.ColumnHeader>
                <Table.ColumnHeader width="90px">
                  <Text fontSize="xs" fontWeight="semibold" color="text.secondary" textTransform="uppercase">
                    {t('reunification.min', 'Min')}
                  </Text>
                </Table.ColumnHeader>
                <Table.ColumnHeader width="90px">
                  <Text fontSize="xs" fontWeight="semibold" color="text.secondary" textTransform="uppercase">
                    {t('reunification.goal', 'Goal')}
                  </Text>
                </Table.ColumnHeader>
                <Table.ColumnHeader width="50px" />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {requirements.map((req) => (
                <StationRequirementRow
                  key={req.public_id}
                  requirement={req}
                  templateId={templateId}
                />
              ))}
            </Table.Body>
          </Table.Root>
        </Box>
      ) : (
        <Text fontSize="sm" color="text.secondary">
          {t('reunification.noStationRequirementsConfigured', 'No station requirements configured')}
        </Text>
      )}

      <AddStationsDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        availableStations={availableStations}
        onAdd={handleAddStations}
      />
    </Box>
  );
}

const PlanTemplateForm: React.FC<PlanTemplateFormProps> = ({ templateId }) => {
  const { t } = useTranslation();
  const { watch, setValue, formState: { errors } } =
    useFormContext<PlanTemplateFormValues>();
  const values = watch();
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setValue(name as keyof PlanTemplateFormValues, value);
  };

  return (
    <VStack gap={4} align="stretch" data-e2e="plan-template-form">
      <Field.Root invalid={!!errors.name} data-e2e="name">
        <Field.Label>{t('reunification.planName', 'Plan Name')}</Field.Label>
        <InputField
          name="name"
          placeholder={t('reunification.enterPlanName', 'Enter plan name')}
          value={values.name}
          onChange={handleChange}
          data-e2e="plan-name-input"
        />
        {errors.name && <Field.ErrorText data-e2e="plan-name-error">{errors.name.message ? t(errors.name.message) : null}</Field.ErrorText>}
      </Field.Root>

      <Field.Root invalid={!!errors.description} data-e2e="description">
        <Field.Label>{t('common.description', 'Description')}</Field.Label>
        <TextareaField
          name="description"
          placeholder={t('reunification.describePlan', 'Describe the reunification plan')}
          value={values.description}
          onChange={handleChange}
          rows={3}
          data-e2e="plan-description-textarea"
        />
        {errors.description && (
          <Field.ErrorText data-e2e="plan-description-error">{errors.description.message ? t(errors.description.message) : null}</Field.ErrorText>
        )}
      </Field.Root>

      <Field.Root invalid={!!errors.incident_type} data-e2e="incident_type">
        <SelectInput
          label={t('reunification.incidentType', 'Incident Type')}
          name="incident_type"
          value={values.incident_type}
          onChange={(val: string) => setValue('incident_type', val as IncidentType)}
          option={INCIDENT_TYPE_OPTIONS}
          placeholder={t('reunification.selectIncidentType', 'Select incident type')}
          e2eTitle="plan-incident-type-select"
        />
        {errors.incident_type && <Field.ErrorText data-e2e="plan-incident-type-error">{errors.incident_type.message ? t(errors.incident_type.message) : null}</Field.ErrorText>}
      </Field.Root>

      <Field.Root invalid={!!errors.reunification_mode} data-e2e="reunification_mode">
        <SelectInput
          label={t('reunification.reunificationMode', 'Reunification Mode')}
          name="reunification_mode"
          value={values.reunification_mode}
          onChange={(val: string) => setValue('reunification_mode', val as ReunificationMode)}
          option={REUNIFICATION_MODE_OPTIONS}
          placeholder={t('reunification.selectMode', 'Select mode')}
          e2eTitle="plan-mode-select"
        />
        {errors.reunification_mode && <Field.ErrorText data-e2e="plan-reunification-mode-error">{errors.reunification_mode.message ? t(errors.reunification_mode.message) : null}</Field.ErrorText>}
      </Field.Root>

      <Field.Root invalid={!!errors.scale} data-e2e="scale">
        <SelectInput
          label={t('reunification.scale', 'Plan Scale')}
          name="scale"
          value={values.scale}
          onChange={(val: string) => setValue('scale', val as PlanScale)}
          option={PLAN_SCALE_OPTIONS}
          placeholder={t('reunification.selectScale', 'Select scale')}
          e2eTitle="plan-scale-select"
        />
        {errors.scale && <Field.ErrorText data-e2e="plan-scale-error">{errors.scale.message ? t(errors.scale.message) : null}</Field.ErrorText>}
      </Field.Root>

      <Field.Root invalid={!!errors.estimated_duration_hours} data-e2e="estimated_duration_hours">
        <Field.Label>{t('reunification.estimatedDuration', 'Estimated Duration (hours)')}</Field.Label>
        <InputField
          name="estimated_duration_hours"
          type="number"
          min={0}
          placeholder="e.g. 4"
          value={values.estimated_duration_hours}
          onChange={handleChange}
          data-e2e="plan-duration-input"
        />
        {errors.estimated_duration_hours && (
          <Field.ErrorText data-e2e="plan-estimated-duration-error">{errors.estimated_duration_hours.message ? t(errors.estimated_duration_hours.message) : null}</Field.ErrorText>
        )}
      </Field.Root>

      <Field.Root invalid={!!errors.special_procedures} data-e2e="special_procedures">
        <Field.Label>{t('reunification.specialProcedures', 'Special Procedures')}</Field.Label>
        <TextareaField
          name="special_procedures"
          placeholder={t('reunification.specialProceduresPlaceholder', 'Any special procedures for this plan')}
          value={values.special_procedures}
          onChange={handleChange}
          rows={3}
          data-e2e="plan-special-procedures-textarea"
        />
        {errors.special_procedures && (
          <Field.ErrorText data-e2e="plan-special-procedures-error">{errors.special_procedures.message ? t(errors.special_procedures.message) : null}</Field.ErrorText>
        )}
      </Field.Root>

      <Field.Root data-e2e="default_leo_interview">
        <Switch.Root
          checked={values.default_leo_interview}
          onCheckedChange={(details) =>
            setValue('default_leo_interview', details.checked)
          }
        >
          <Switch.HiddenInput data-e2e="plan-require-leo-switch" />
          <Switch.Control>
            <Switch.Thumb />
          </Switch.Control>
          <Switch.Label>{t('reunification.requireLeo', 'Require LEO Interview by Default')}</Switch.Label>
        </Switch.Root>
      </Field.Root>

      <Field.Root data-e2e="default_counseling">
        <Switch.Root
          checked={values.default_counseling}
          onCheckedChange={(details) =>
            setValue('default_counseling', details.checked)
          }
        >
          <Switch.HiddenInput data-e2e="plan-require-counseling-switch" />
          <Switch.Control>
            <Switch.Thumb />
          </Switch.Control>
          <Switch.Label>{t('reunification.requireCounseling', 'Require Counseling by Default')}</Switch.Label>
        </Switch.Root>
      </Field.Root>

      <Field.Root data-e2e="is_published">
        <Switch.Root
          checked={values.is_published}
          onCheckedChange={(details) =>
            setValue('is_published', details.checked)
          }
        >
          <Switch.HiddenInput data-e2e="plan-published-switch" />
          <Switch.Control>
            <Switch.Thumb />
          </Switch.Control>
          <Switch.Label>{t('reunification.published', 'Published')}</Switch.Label>
        </Switch.Root>
      </Field.Root>

      <Separator />

      <Text fontWeight="semibold" fontSize="sm">
        {t('reunification.guardianVerification', 'Guardian Verification')}
      </Text>

      <Field.Root data-e2e="guardian_verification_required">
        <Switch.Root
          checked={values.guardian_verification_required}
          onCheckedChange={(details) =>
            setValue('guardian_verification_required', details.checked)
          }
        >
          <Switch.HiddenInput data-e2e="plan-guardian-verification-switch" />
          <Switch.Control>
            <Switch.Thumb />
          </Switch.Control>
          <Switch.Label>{t('reunification.requireGuardianVerification', 'Require Guardian Verification')}</Switch.Label>
        </Switch.Root>
      </Field.Root>

      <Field.Root data-e2e="require_id_verification">
        <Switch.Root
          checked={values.require_id_verification}
          onCheckedChange={(details) =>
            setValue('require_id_verification', details.checked)
          }
        >
          <Switch.HiddenInput data-e2e="plan-id-verification-switch" />
          <Switch.Control>
            <Switch.Thumb />
          </Switch.Control>
          <Switch.Label>{t('reunification.requireIdVerification', 'Require ID Verification')}</Switch.Label>
        </Switch.Root>
      </Field.Root>

      <Field.Root data-e2e="require_photo_capture">
        <Switch.Root
          checked={values.require_photo_capture}
          onCheckedChange={(details) =>
            setValue('require_photo_capture', details.checked)
          }
        >
          <Switch.HiddenInput data-e2e="plan-photo-capture-switch" />
          <Switch.Control>
            <Switch.Thumb />
          </Switch.Control>
          <Switch.Label>{t('reunification.requirePhotoCapture', 'Require Photo Capture')}</Switch.Label>
        </Switch.Root>
      </Field.Root>

      <Field.Root data-e2e="require_signature">
        <Switch.Root
          checked={values.require_signature}
          onCheckedChange={(details) =>
            setValue('require_signature', details.checked)
          }
        >
          <Switch.HiddenInput data-e2e="plan-signature-switch" />
          <Switch.Control>
            <Switch.Thumb />
          </Switch.Control>
          <Switch.Label>{t('reunification.requireSignature', 'Require Signature')}</Switch.Label>
        </Switch.Root>
      </Field.Root>

      <Separator />

      <Text fontWeight="semibold" fontSize="sm">
        {t('account.notifications', 'Notifications')}
      </Text>

      <Field.Root data-e2e="notify_on_activation">
        <Switch.Root
          checked={values.notify_on_activation}
          onCheckedChange={(details) => {
            setValue('notify_on_activation', details.checked);
            if (details.checked && values.notification_channels.length === 0) {
              setValue('notification_channels', ['push']);
            }
          }}
        >
          <Switch.HiddenInput data-e2e="plan-notify-on-activation-switch" />
          <Switch.Control>
            <Switch.Thumb />
          </Switch.Control>
          <Switch.Label>{t('reunification.notifyOnActivation', 'Notify on Activation')}</Switch.Label>
        </Switch.Root>
      </Field.Root>

      {values.notify_on_activation && (
        <Box>
          <Text fontSize="sm" color="text.secondary" mb={2}>
            {t('reunification.notificationChannels', 'Notification Channels')}
          </Text>
          <VStack gap={2} align="stretch">
            {NOTIFICATION_CHANNEL_OPTIONS.map((channel) => (
              <Field.Root key={channel.value}>
                <Switch.Root
                  checked={values.notification_channels.includes(channel.value)}
                  disabled={
                    values.notification_channels.includes(channel.value) &&
                    values.notification_channels.length === 1
                  }
                  onCheckedChange={(details) => {
                    const current = values.notification_channels;
                    if (details.checked) {
                      setValue('notification_channels', [
                        ...current,
                        channel.value,
                      ]);
                    } else {
                      setValue(
                        'notification_channels',
                        current.filter((c) => c !== channel.value),
                      );
                    }
                  }}
                >
                  <Switch.HiddenInput data-e2e={`plan-notification-channel-${channel.value}-switch`} />
                  <Switch.Control>
                    <Switch.Thumb />
                  </Switch.Control>
                  <Switch.Label>{t(`reunification.channel.${channel.value}`, channel.label)}</Switch.Label>
                </Switch.Root>
              </Field.Root>
            ))}
          </VStack>
        </Box>
      )}

      {templateId && (
        <>
          <Separator />
          <RoleRequirementsSection templateId={templateId} />
          <Separator />
          <StationRequirementsSection templateId={templateId} />
          <Separator />
          <FloorplansSection templateId={templateId} />
          <Separator />
          <ApplicableSitesSection templateId={templateId} />
          <Separator />
          <LabelTemplatesSection templateId={templateId} />
        </>
      )}
    </VStack>
  );
};

export default PlanTemplateForm;
